import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/core/styles';
import { solicitudesService } from '../services';
import { authenticationService } from '../services';
import SeccionDatosPrincipalesIndividuo from '../components/solicitud/SeccionDatosPrincipalesIndividuo';
import SeccionDatosPersonalesIndividuo from '../components/solicitud/SeccionDatosPersonalesIndividuo';
import SeccionDomicilio from '../components/solicitud/SeccionDomicilio';
import SeccionDatosFiscales from '../components/solicitud/SeccionDatosFiscales';
import SeccionDatosFiscalesExterior from '../components/solicitud/SeccionDatosFiscalesExterior';
import SeccionCuentasBancarias from '../components/solicitud/SeccionCuentasBancarias';
import SeccionCuentasBancariasExterior from '../components/solicitud/SeccionCuentasBancariasExterior';
import SeccionDeclaraciones from '../components/solicitud/SeccionDeclaraciones';
import SeccionDDJJActividadOrigenFondos from '../components/solicitud/SeccionDDJJActividadOrigenFondos';
import SeccionPerfilInversor from '../components/solicitud/SeccionPerfilInversor';
import SeccionDeclaracionIngresos from '../components/solicitud/SeccionDeclaracionIngresos';
import SeccionTerminosCondiciones from '../components/solicitud/SeccionTerminosCondiciones';
import SeccionCoTitulares from '../components/solicitud/SeccionCoTitulares';
import SeccionDatosPrincipalesEmpresa from '../components/solicitud/SeccionDatosPrincipalesEmpresa';
import SeccionDatosOrganizacionEmpresa from '../components/solicitud/SeccionDatosOrganizacionEmpresa';
import SeccionDatosRegistroEmpresa from '../components/solicitud/SeccionDatosRegistroEmpresa';
import SeccionDocumentacionRespaldatoria from '../components/solicitud/SeccionDocumentacionRespaldatoria';
import SeccionFirmantes from '../components/solicitud/SeccionFirmantes';
import SeccionAccionistas from '../components/solicitud/SeccionAccionistas';
import SeccionGeneral from '../components/solicitud/SeccionGeneral';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  sidebar: {
    width: 280,
    minWidth: 280,
    backgroundColor: '#f5f5f5',
    borderRight: '1px solid #e0e0e0',
    height: 'fit-content',
    position: 'sticky',
    top: theme.spacing(2),
  },
  sidebarHeader: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
  menuItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  },
  menuItemSelected: {
    backgroundColor: '#bbdefb',
    borderLeft: '3px solid #2196f3',
    '&:hover': {
      backgroundColor: '#90caf9',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  contentHeader: {
    marginBottom: theme.spacing(3),
  },
}));

const DetalleSolicitud = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solicitud, setSolicitud] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const solicitudes = await solicitudesService.obtenerSolicitudes();
      const solicitudEncontrada = solicitudes.find(s => s.id === parseInt(id));

      if (!solicitudEncontrada) {
        setError('Solicitud no encontrada');
        return;
      }

      setSolicitud(solicitudEncontrada);
      setTipo(solicitudEncontrada.tipo);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!authenticationService.checkSessionValidity()) {
      history.push('/login');
      return;
    }

    // Si la solicitud viene en el state (desde el listado), usarla directamente
    if (location.state?.solicitud) {
      const solicitudFromState = location.state.solicitud;
      setSolicitud(solicitudFromState);
      setTipo(solicitudFromState.tipo);
      setLoading(false);
    } else {
      // Si no viene en el state (acceso directo por URL), cargar desde el backend
      cargarDatos();
    }
  }, [id, history, location.state, cargarDatos]);

  const handleMenuClick = (index) => {
    setTabValue(index);
  };

  const getTabsIndividuo = () => [
    { label: 'General', component: <SeccionGeneral solicitud={solicitud} tipo={tipo} onSolicitudUpdate={cargarDatos} /> },
    { label: 'Datos Principales', component: <SeccionDatosPrincipalesIndividuo solicitudId={id} /> },
    { label: 'Datos Personales', component: <SeccionDatosPersonalesIndividuo solicitudId={id} /> },
    { label: 'Domicilio', component: <SeccionDomicilio solicitudId={id} tipo="individuo" /> },
    { label: 'Datos Fiscales', component: <SeccionDatosFiscales solicitudId={id} tipo="individuo" /> },
    { label: 'Datos Fiscales Exterior', component: <SeccionDatosFiscalesExterior solicitudId={id} tipo="individuo" /> },
    { label: 'Cuentas Bancarias', component: <SeccionCuentasBancarias solicitudId={id} tipo="individuo" /> },
    { label: 'Cuentas Bancarias Exterior', component: <SeccionCuentasBancariasExterior solicitudId={id} tipo="individuo" /> },
    { label: 'Declaraciones', component: <SeccionDeclaraciones solicitudId={id} tipo="individuo" /> },
    { label: 'Declaración Jurada de Origen y Licitud de fondos', component: <SeccionDDJJActividadOrigenFondos solicitudId={id} /> },
    { label: 'Perfil Inversor', component: <SeccionPerfilInversor solicitudId={id} tipo="individuo" /> },
    { label: 'Declaración Ingresos', component: <SeccionDeclaracionIngresos solicitudId={id} /> },
    { label: 'Términos y Condiciones', component: <SeccionTerminosCondiciones solicitudId={id} tipo="individuo" /> },
    { label: 'Co-titulares', component: <SeccionCoTitulares solicitudId={id} /> }
  ];

  const getTabsEmpresa = () => [
    { label: 'General', component: <SeccionGeneral solicitud={solicitud} tipo={tipo} onSolicitudUpdate={cargarDatos} /> },
    { label: 'Datos Principales', component: <SeccionDatosPrincipalesEmpresa solicitudId={id} /> },
    { label: 'Datos Organización', component: <SeccionDatosOrganizacionEmpresa solicitudId={id} /> },
    { label: 'Domicilio', component: <SeccionDomicilio solicitudId={id} tipo="empresa" /> },
    { label: 'Datos Fiscales', component: <SeccionDatosFiscales solicitudId={id} tipo="empresa" /> },
    { label: 'Datos Fiscales Exterior', component: <SeccionDatosFiscalesExterior solicitudId={id} tipo="empresa" /> },
    { label: 'Registro', component: <SeccionDatosRegistroEmpresa solicitudId={id} /> },
    { label: 'Cuentas Bancarias', component: <SeccionCuentasBancarias solicitudId={id} tipo="empresa" /> },
    { label: 'Cuentas Bancarias Exterior', component: <SeccionCuentasBancariasExterior solicitudId={id} tipo="empresa" /> },
    { label: 'Declaraciones', component: <SeccionDeclaraciones solicitudId={id} tipo="empresa" /> },
    { label: 'Declaración Jurada de Origen y Licitud de fondos', component: <SeccionDDJJActividadOrigenFondos solicitudId={id} /> },
    { label: 'Perfil Inversor', component: <SeccionPerfilInversor solicitudId={id} tipo="empresa" /> },
    { label: 'Documentación Respaldatoria', component: <SeccionDocumentacionRespaldatoria solicitudId={id} /> },
    { label: 'Firmantes', component: <SeccionFirmantes solicitudId={id} /> },
    { label: 'Accionistas', component: <SeccionAccionistas solicitudId={id} /> },
    { label: 'Términos y Condiciones', component: <SeccionTerminosCondiciones solicitudId={id} tipo="empresa" /> }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Box
          p={3}
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            color: '#c62828'
          }}
        >
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  const tabs = tipo === 'INDIVIDUO' ? getTabsIndividuo() : getTabsEmpresa();

  return (
    <Box>
      <Box className={classes.contentHeader} display="flex" alignItems="center" flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push('/solicitudes')}
          style={{ marginRight: '1rem', marginBottom: '0.5rem' }}
        >
          Volver
        </Button>
        <Typography variant="h4" component="h1" style={{ marginRight: '1rem', marginBottom: '0.5rem' }}>
          Detalle de Solicitud - {solicitud?.titular || ''}
        </Typography>
      </Box>

      <Box className={classes.root}>
        <Paper className={classes.sidebar}>
          <Box className={classes.sidebarHeader}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Secciones
            </Typography>
          </Box>
          <List style={{ padding: 0 }}>
            {tabs.map((tab, index) => (
              <React.Fragment key={index}>
                <ListItem
                  className={`${classes.menuItem} ${tabValue === index ? classes.menuItemSelected : ''}`}
                  onClick={() => handleMenuClick(index)}
                >
                  <ListItemText
                    primary={tab.label}
                    primaryTypographyProps={{
                      style: {
                        fontWeight: tabValue === index ? 600 : 400,
                        fontSize: '14px'
                      }
                    }}
                  />
                </ListItem>
                {index < tabs.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>

        <Box className={classes.content}>
          {tabs[tabValue]?.component}
        </Box>
      </Box>
    </Box>
  );
};

export default DetalleSolicitud;
